import React, { useContext, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Calendar,
  Clock,
  TrendingUp,
  CheckCircle,
  XCircle,
  AlertCircle,
  Filter,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  format,
  addMonths,
  subMonths,
} from "date-fns";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import StatsCard from "../../components/common/StatsCard";
import { AuthContext } from "../../contexts/AuthContext";
import { getMyStudentProfile } from "../../services/students";
import { getMyAttendance } from "../../services/attendance";

const StudentAttendance: React.FC = () => {
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().slice(0, 7)
  );

  const { data: studentData, isLoading: studentLoading } = useQuery({
    queryKey: ["student-profile-access-check"],
    queryFn: () => getMyStudentProfile(),
    enabled: !!user?.id,
  });

  const student = studentData?.data;

  // Prevent e-course students from accessing regular student pages
  if (student && student.studentType === "ecourse") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center max-w-md">
          <div className="mb-4">
            <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-orange-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Access Restricted
          </h2>
          <p className="text-gray-600 mb-4">
            This page is not available for e-course students. Please use the
            e-learning dashboard.
          </p>
          <a
            href="/student/ecourse-dashboard"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to E-Learning Dashboard
          </a>
        </div>
      </div>
    );
  }

  // Fetch student's own attendance records
  const { data: attendanceData, isLoading: recordsLoading } = useQuery({
    queryKey: ["my-attendance-records", selectedMonth],
    queryFn: () => {
      // Calculate the first and last day of the selected month
      const [year, month] = selectedMonth.split("-").map(Number);
      const startDate = new Date(year, month - 1, 1); // First day of month
      const endDate = new Date(year, month, 0); // Last day of month

      // Format dates as YYYY-MM-DD
      const dateFrom = startDate.toISOString().split("T")[0];
      const dateTo = endDate.toISOString().split("T")[0];

      return getMyAttendance({
        dateFrom,
        dateTo,
        limit: 100,
      });
    },
    enabled: !!user?.id,
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });

  const isLoading = studentLoading || recordsLoading;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <LoadingSpinner />
      </div>
    );
  }

  const records = attendanceData?.data || [];
  const summary = attendanceData?.summary || {
    total: 0,
    present: 0,
    absent: 0,
    late: 0,
    excused: 0,
    attendancePercentage: 0,
  };

  // Calculate total school days (weekdays) in the selected month
  const [year, month] = selectedMonth.split("-").map(Number);
  const currentMonth = new Date(year, month - 1);
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const allDaysInMonth = eachDayOfInterval({
    start: monthStart,
    end: monthEnd,
  });

  // Count only weekdays (Monday to Friday)
  const totalSchoolDays = allDaysInMonth.filter((day) => {
    const dayOfWeek = day.getDay();
    return dayOfWeek !== 0 && dayOfWeek !== 6; // 0 = Sunday, 6 = Saturday
  }).length;

  // Calculate attendance statistics from summary
  const totalDays = summary.total;
  const presentDays = summary.present;
  const lateDays = summary.late;

  // Calculate absent days as total school days minus days present
  const absentDays = totalSchoolDays - presentDays;

  // Calculate attendance rate based on school days
  const attendanceRate =
    totalSchoolDays > 0 ? (presentDays / totalSchoolDays) * 100 : 0;

  const getStatusColor = (status: string) => {
    switch (status) {
      case "present":
        return "text-green-600 bg-green-100";
      case "absent":
        return "text-red-600 bg-red-100";
      case "late":
        return "text-yellow-600 bg-yellow-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "present":
        return <CheckCircle className="h-4 w-4" />;
      case "absent":
        return <XCircle className="h-4 w-4" />;
      case "late":
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-3 sm:p-4 lg:p-6">
      {/* Header */}
      <div className="mb-6 lg:mb-8">
        <h1 className="text-xl sm:text-2xl md:text-4xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
          My Attendance
        </h1>
        <p className="text-slate-600 mt-2 text-sm sm:text-lg font-medium">
          Track your attendance records and performance
        </p>
      </div>

      {/* Attendance Statistics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 lg:mb-8">
        {/* Attendance Rate */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 p-4 lg:p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-medium text-slate-600">
                Attendance Rate
              </p>
              <p className="text-2xl lg:text-3xl font-bold text-slate-900 mt-2">
                {attendanceRate.toFixed(1)}%
              </p>
            </div>
            <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-lg">
              <TrendingUp className="h-6 w-6 lg:h-7 lg:w-7 text-white" />
            </div>
          </div>
        </div>

        {/* Present Days */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 p-4 lg:p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-medium text-slate-600">
                Present Days
              </p>
              <p className="text-2xl lg:text-3xl font-bold text-slate-900 mt-2">
                {presentDays}
              </p>
            </div>
            <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center shadow-lg">
              <CheckCircle className="h-6 w-6 lg:h-7 lg:w-7 text-white" />
            </div>
          </div>
        </div>

        {/* Absent Days */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 p-4 lg:p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-medium text-slate-600">
                Absent Days
              </p>
              <p className="text-2xl lg:text-3xl font-bold text-slate-900 mt-2">
                {absentDays}
              </p>
            </div>
            <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-2xl bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center shadow-lg">
              <XCircle className="h-6 w-6 lg:h-7 lg:w-7 text-white" />
            </div>
          </div>
        </div>

        {/* Late Days */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 p-4 lg:p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-medium text-slate-600">
                Late Days
              </p>
              <p className="text-2xl lg:text-3xl font-bold text-slate-900 mt-2">
                {lateDays}
              </p>
            </div>
            <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-2xl bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center shadow-lg">
              <AlertCircle className="h-6 w-6 lg:h-7 lg:w-7 text-white" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* Monthly Overview */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/60 p-4 lg:p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg lg:text-xl font-bold text-slate-900">
              Monthly Overview
            </h2>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-md">
              <Calendar className="h-5 w-5 text-white" />
            </div>
          </div>

          {/* Month Selector */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Select Month
            </label>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm transition-all duration-300 font-medium text-slate-900"
            />
          </div>

          {/* Attendance Summary */}
          <div className="space-y-3">
            <div className="flex items-center justify-between p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200/60 hover:shadow-md transition-all duration-300">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center shadow-md">
                  <CheckCircle className="h-5 w-5 text-white" />
                </div>
                <span className="text-sm font-semibold text-green-900">
                  Present
                </span>
              </div>
              <span className="font-bold text-green-900">
                {presentDays} days
              </span>
            </div>

            <div className="flex items-center justify-between p-4 bg-gradient-to-br from-red-50 to-pink-50 rounded-xl border border-red-200/60 hover:shadow-md transition-all duration-300">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center shadow-md">
                  <XCircle className="h-5 w-5 text-white" />
                </div>
                <span className="text-sm font-semibold text-red-900">
                  Absent
                </span>
              </div>
              <span className="font-bold text-red-900">{absentDays} days</span>
            </div>

            <div className="flex items-center justify-between p-4 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl border border-yellow-200/60 hover:shadow-md transition-all duration-300">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center shadow-md">
                  <AlertCircle className="h-5 w-5 text-white" />
                </div>
                <span className="text-sm font-semibold text-yellow-900">
                  Late
                </span>
              </div>
              <span className="font-bold text-yellow-900">{lateDays} days</span>
            </div>

            <div className="flex items-center justify-between p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200/60 hover:shadow-md transition-all duration-300">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-md">
                  <TrendingUp className="h-5 w-5 text-white" />
                </div>
                <span className="text-sm font-semibold text-blue-900">
                  Total Rate
                </span>
              </div>
              <span className="font-bold text-blue-900">
                {attendanceRate.toFixed(1)}%
              </span>
            </div>
          </div>
        </div>

        {/* Attendance Calendar */}
        <div className="lg:col-span-2">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/60 p-3 sm:p-4 lg:p-6">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h2 className="text-base sm:text-lg lg:text-xl font-bold text-slate-900">
                Attendance Calendar
              </h2>
              <div className="flex items-center gap-1 sm:gap-2">
                <button
                  onClick={() => {
                    const [year, month] = selectedMonth.split("-").map(Number);
                    const newDate = subMonths(new Date(year, month - 1), 1);
                    setSelectedMonth(format(newDate, "yyyy-MM"));
                  }}
                  className="p-1.5 sm:p-2 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5 text-slate-600" />
                </button>
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-md">
                  <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                </div>
                <button
                  onClick={() => {
                    const [year, month] = selectedMonth.split("-").map(Number);
                    const newDate = addMonths(new Date(year, month - 1), 1);
                    setSelectedMonth(format(newDate, "yyyy-MM"));
                  }}
                  className="p-1.5 sm:p-2 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-slate-600" />
                </button>
              </div>
            </div>

            {/* Month Display */}
            <div className="text-center mb-3 sm:mb-4">
              <h3 className="text-base sm:text-lg lg:text-xl font-bold text-slate-900">
                {new Date(selectedMonth + "-01").toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                })}
              </h3>
            </div>

            {/* Calendar Grid */}
            <div className="mb-3 sm:mb-4">
              {/* Weekday headers */}
              <div className="grid grid-cols-7 gap-1 sm:gap-1.5 mb-1 sm:mb-2">
                {["S", "M", "T", "W", "T", "F", "S"].map((day, i) => (
                  <div
                    key={i}
                    className="text-center text-xs sm:text-sm font-bold text-slate-600 py-1 sm:py-1.5"
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar days */}
              <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
                {(() => {
                  const [year, month] = selectedMonth.split("-").map(Number);
                  const currentMonth = new Date(year, month - 1);
                  const monthStart = startOfMonth(currentMonth);
                  const monthEnd = endOfMonth(currentMonth);
                  const days = eachDayOfInterval({
                    start: monthStart,
                    end: monthEnd,
                  });

                  // Add empty cells for days before month starts
                  const startDay = monthStart.getDay();
                  const emptyCells = Array(startDay).fill(null);

                  return (
                    <>
                      {emptyCells.map((_, i) => (
                        <div key={`empty-${i}`} />
                      ))}
                      {days.map((day) => {
                        const dayRecords = records.filter((r: any) =>
                          isSameDay(new Date(r.date), day)
                        );
                        const hasAttendance = dayRecords.length > 0;
                        const isWeekend =
                          day.getDay() === 0 || day.getDay() === 6;

                        return (
                          <div
                            key={day.toISOString()}
                            className={`aspect-square flex items-center justify-center p-0.5 rounded-md sm:rounded-lg text-center text-xs sm:text-sm font-medium transition-all ${
                              isWeekend
                                ? "bg-slate-50 text-slate-400"
                                : hasAttendance
                                ? "bg-gradient-to-br from-green-500 to-emerald-500 text-white shadow-sm sm:shadow-md hover:shadow-lg cursor-pointer"
                                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                            }`}
                            title={
                              hasAttendance
                                ? `Attended on ${format(day, "MMM d")}`
                                : isWeekend
                                ? "Weekend"
                                : "No attendance"
                            }
                          >
                            {format(day, "d")}
                          </div>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Summary */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-3 sm:p-4">
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-blue-900">
                    {records.length}
                  </p>
                  <p className="text-xs font-medium text-blue-700 mt-1">
                    Days Attended
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-blue-900">
                    {attendanceRate.toFixed(1)}%
                  </p>
                  <p className="text-xs font-medium text-blue-700 mt-1">
                    Attendance Rate
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Analysis */}
      <div className="mt-8">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/60 p-6">
          <h2 className="text-lg lg:text-xl font-bold text-slate-900 mb-6">
            Attendance Performance
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-200/60 hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center shadow-lg">
                <TrendingUp className="h-8 w-8 text-white" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">
                Overall Rating
              </h3>
              <p className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                {attendanceRate >= 90
                  ? "Excellent"
                  : attendanceRate >= 75
                  ? "Good"
                  : attendanceRate >= 60
                  ? "Average"
                  : "Needs Improvement"}
              </p>
              <p className="text-sm text-slate-600 mt-2 font-medium">
                {attendanceRate.toFixed(1)}% attendance
              </p>
            </div>

            <div className="text-center p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border border-green-200/60 hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center shadow-lg">
                <CheckCircle className="h-8 w-8 text-white" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Consistency</h3>
              <p className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                {absentDays <= 2 ? "High" : absentDays <= 5 ? "Medium" : "Low"}
              </p>
              <p className="text-sm text-slate-600 mt-2 font-medium">
                {absentDays} absent days
              </p>
            </div>

            <div className="text-center p-6 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl border border-yellow-200/60 hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg">
                <Clock className="h-8 w-8 text-white" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Punctuality</h3>
              <p className="text-2xl font-bold bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">
                {lateDays <= 1 ? "Excellent" : lateDays <= 3 ? "Good" : "Fair"}
              </p>
              <p className="text-sm text-slate-600 mt-2 font-medium">
                {lateDays} late days
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentAttendance;
